'use client';

import { createUuid } from '@core/utils';
import { captureException } from '@sentry/nextjs';
import { useMutation } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useShallow } from 'zustand/react/shallow';

import { AuthApi, UnauthApi } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { LOCAL_STORAGE_KEY } from '@/stores/GlobalStore';
import { isBot } from '@/utils/isBot';

const WelloTrackerComponent = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isLogin = useAuthStore((state) => state.isLogin);
  const [osType, viewportType] = useDeviceInfoStore(
    useShallow((state) => [state.osType, state.viewportType] as const),
  );

  const { mutate: postWelloTrack } = useMutation({
    mutationFn: () => {
      const getOrCreateUuid = () => {
        try {
          const storedUuid = localStorage.getItem(LOCAL_STORAGE_KEY.WELLO_UUID);
          if (storedUuid) {
            return storedUuid;
          }
          const newUuid = createUuid();
          localStorage.setItem(LOCAL_STORAGE_KEY.WELLO_UUID, newUuid);

          return newUuid;
        } catch (error) {
          captureException(error);

          return createUuid();
        }
      };

      const welloUuid = getOrCreateUuid();

      const previousUrl = (() => {
        if (typeof window === 'undefined') return '';

        if (window.history.state && window.history.state.url) {
          return window.history.state.url;
        }

        return document.referrer || '';
      })();

      const fullUrl = `${pathname}${
        searchParams?.toString() ? `?${searchParams.toString()}` : ''
      }`;

      const requestBody = {
        eventName: 'pageView',
        dictionary: {
          url: fullUrl,
          osType,
          viewportType,
          previousUrl,
          welloUuid,
        },
      };

      return isLogin
        ? AuthApi['post:/wello/v2/auth/wello-track']({ body: requestBody })
        : UnauthApi['post:/wello/v2/allows/wello-track']({ body: requestBody });
    },
  });

  useEffect(() => {
    if (isBot()) return;
    if (isLogin !== undefined) {
      postWelloTrack();
    }
  }, [pathname, postWelloTrack, isLogin]);

  return <></>;
};

export const WelloTracker = () => {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <WelloTrackerComponent />
      </Suspense>
    </ErrorBoundary>
  );
};
